import { Handshake } from "lucide-react";
import { Link } from "react-router";
import {
  useCreatePath,
  useListContext,
  useRecordContext,
  useTranslate,
} from "ra-core";
import { ReferenceManyField } from "@/components/admin/reference-many-field";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

import { Avatar as ContactAvatar } from "../contacts/Avatar";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Company } from "../types";
import { CompanyAvatar } from "./CompanyAvatar";
import { getCompanyRelationshipLabel } from "./relationshipTypes";

export const CompanyCard = (props: { record?: Company }) => {
  const createPath = useCreatePath();
  const record = useRecordContext<Company>(props);
  const translate = useTranslate();
  const { companySectors, currency } = useConfigurationContext();
  if (!record) return null;

  const sector = companySectors.find((s) => s.value === record.sector);
  const sectorLabel = sector?.label;
  const relationshipLabel = getCompanyRelationshipLabel(
    translate,
    record.relationship_type,
  );

  return (
    <Link
      to={createPath({
        resource: "companies",
        id: record.id,
        type: "show",
      })}
      className="no-underline"
    >
      <Card className="min-h-[220px] flex flex-col justify-between p-4 hover:bg-muted">
        <div className="flex flex-col items-center gap-1">
          <CompanyAvatar />
          <div className="text-center mt-1">
            <h6 className="text-sm font-medium">{record.name}</h6>
            <div className="mt-1 flex flex-wrap items-center justify-center gap-1">
              <Badge variant="outline" className="text-[10px]">
                {relationshipLabel}
              </Badge>
              {sectorLabel ? (
                <p className="text-xs text-muted-foreground">{sectorLabel}</p>
              ) : null}
            </div>
          </div>
        </div>
        <div className="flex flex-row w-full justify-between gap-2">
          <div className="flex items-center">
            {record.nb_contacts ? (
              <ReferenceManyField reference="contacts" target="company_id">
                <AvatarGroupIterator />
              </ReferenceManyField>
            ) : null}
          </div>
          {record.nb_projects ? (
            <div className="flex items-center ml-2 gap-0.5">
              <Handshake className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium">{record.nb_projects}</span>
              <span className="text-xs text-muted-foreground">
                {translate("resources.projects.name", {
                  smart_count: record.nb_projects ?? 0,
                  _: "Project |||| Projects",
                })}
              </span>
            </div>
          ) : null}
        </div>
        <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-[11px] leading-tight text-muted-foreground">
          <span className="min-w-0 break-words">
            {translate("resources.companies.fields.contract_revenue_total")}:{" "}
            {(record.contract_revenue_total ?? 0).toLocaleString("en-US", {
              style: "currency",
              currency,
              maximumFractionDigits: 0,
            })}
          </span>
          <span className="min-w-0 break-words">
            {translate("resources.companies.fields.gross_profit_total")}:{" "}
            {(record.gross_profit_total ?? 0).toLocaleString("en-US", {
              style: "currency",
              currency,
              maximumFractionDigits: 0,
            })}
          </span>
          <span className="col-span-2 min-w-0 break-words">
            {translate("resources.companies.fields.margin_average")}:{" "}
            {((record.margin_average ?? 0) * 100).toFixed(1)}%
          </span>
        </div>
      </Card>
    </Link>
  );
};

const AvatarGroupIterator = () => {
  const { data, total, error, isPending } = useListContext();
  if (isPending || error) return null;

  const MAX_AVATARS = 3;
  return (
    <div className="*:data-[slot=avatar]:ring-background flex -space-x-0.5 *:data-[slot=avatar]:ring-2 *:data-[slot=avatar]:grayscale-50">
      {data.slice(0, MAX_AVATARS).map((record: any) => (
        <ContactAvatar
          key={record.id}
          record={record}
          width={25}
          height={25}
          title={`${record.first_name} ${record.last_name}`}
        />
      ))}
      {total > MAX_AVATARS && (
        <span
          className="relative flex size-8 shrink-0 overflow-hidden rounded-full w-[25px] h-[25px]"
          data-slot="avatar"
        >
          <span className="bg-muted flex size-full items-center justify-center rounded-full text-[10px]">
            +{total - MAX_AVATARS}
          </span>
        </span>
      )}
    </div>
  );
};
